/**
 * The expansion's two WALK-IN buildings (the sim does not block their footprint, as it does not the
 * hangar's): the headquarters of mission 21, with the safe inside it, and the cell block mission 32
 * starts in. The camera looks down from the south at 45 degrees, so a roof or a south wall of height
 * h hides h metres of floor behind it. Both are therefore drawn CUT AWAY: a full rear wall and side
 * walls, a strip of roof along the rear wall only, and a south front that is only a knee wall. No
 * rafters, ties or lintel: measured with the camera's own rays (`tests/unit/kit3WalkIn.test.ts`), every
 * beam 3 m up costs a band or a stripe of the man beneath it, and the cut-away reads without them.
 */

import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { sided, snowPatch } from './parts';

interface Shell {
  halfW: number;
  halfD: number;
  /** Wall height; the rear roof strip lies on it. */
  h: number;
  wall: number;
  /** Palette ramp of the masonry. */
  ramp: string;
  floor: string;
  /** How far the roof strip reaches south from the rear wall's outer face. */
  roofDepth: number;
  /** Half-width of the doorway in the middle of the south front. */
  doorHalf: number;
}

/** Floor, three walls (their tops capped light, so the plan of the room reads), the rear roof strip, the knee wall. */
function shell(mb: MeshBuilder, p: Palette, s: Shell): void {
  const { halfW, halfD, h, wall } = s;
  const mid = p.color(`${s.ramp}.mid`);
  const lo = p.color(`${s.ramp}.lo`);
  mb.box(halfW * 2 - 0.2, 0.08, halfD * 2 - 0.1, { all: p.color(`${s.floor}.lo`), top: p.color(`${s.floor}.mid`) });
  // Inward faces take the dark step, so the room reads as shade (as the hangar's do).
  mb.box(halfW * 2, h, wall, { all: mid, front: lo }, place(0, 0, -halfD + wall / 2));
  const hi = p.color(`${s.ramp}.hi`);
  mb.box(wall, h, halfD * 2 - wall, { all: mid, right: lo, top: hi }, place(-halfW + wall / 2, 0, wall / 2));
  mb.box(wall, h, halfD * 2 - wall, { all: mid, left: lo, top: hi }, place(halfW - wall / 2, 0, wall / 2));

  const roofZ = -halfD - 0.2 + s.roofDepth / 2;
  mb.box(halfW * 2 + 0.4, 0.2, s.roofDepth, { all: p.color('roof.0'), top: p.color('roof.1') }, place(0, h, roofZ));
  snowPatch(mb, p, -halfW * 0.45, h + 0.2, roofZ, halfW * 0.7, s.roofDepth * 0.55);
  snowPatch(mb, p, halfW * 0.4, h + 0.2, roofZ - 0.05, halfW * 0.5, s.roofDepth * 0.45);

  // South front: a knee wall either side of the doorway, with the enemy's board on it beside the door.
  // No lintel and no posts: anything standing 3 m high in the south front hides what is behind it.
  const z = halfD - wall / 2;
  const run = halfW - wall - s.doorHalf;
  for (const sx of [-1, 1]) {
    mb.box(run, 0.5, wall, { all: mid, top: hi }, place(sx * (s.doorHalf + run / 2), 0, z));
  }
  mb.box(1.3, 0.32, 0.06, p.color('enemyAccent.0'), place(-s.doorHalf - 0.85, 0.12, halfD + 0.02));
}

/**
 * Headquarters (mission 21): brick in the temperate palette, concrete elsewhere. Inside, against the
 * rear wall where the camera sees them: the enemy's banner, a map board, and a desk with a lamp. The
 * rest of the floor is left clear for the `safe`, wherever the generator stands it.
 */
export const hqBuilding: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const ramp = p.biome === 'temperate' ? 'brick' : 'concrete';
  shell(mb, p, {
    halfW: 5, halfD: 3.5, h: 3.0, wall: 0.35, ramp, floor: 'timber', roofDepth: 2.3, doorHalf: 0.85,
  });
  const inner = -3.5 + 0.35;
  mb.box(1.3, 1.7, 0.06, p.color('enemyAccent.0'), place(-0.2, 1.0, inner + 0.03));
  mb.box(1.7, 1.0, 0.06, p.color('paper.0'), place(2.9, 1.3, inner + 0.03));
  mb.box(1.8, 0.8, 0.9, { all: p.color('timber.mid'), top: p.color('timber.hi') }, place(-3.3, 0, -2.4));
  mb.box(0.22, 0.26, 0.22, p.color('fire.2'), place(-3.9, 0.8, -2.5));
  return mb;
};

/** Safe: a steel cube with its door, dial and brass handle on the SOUTH face. Five boxes - the small budget. */
export const safe: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.98, 0.1, 0.94, p.color('ink.1'));
  mb.box(0.94, 1.08, 0.9, { all: p.color('metal.lo'), top: p.color('metal.mid') }, place(0, 0.1, 0));
  mb.box(0.72, 0.84, 0.06, p.color('metal.mid'), place(0, 0.22, 0.46));
  mb.box(0.18, 0.18, 0.06, p.color('metal.hi'), place(-0.12, 0.66, 0.51));
  mb.box(0.1, 0.3, 0.07, p.color('fire.2'), place(0.2, 0.5, 0.51));
  return mb;
};

/** Cells along the rear wall: inner width each, and the x of each cell's centre. The middle door stands ajar. */
export const CELL_WIDTH = 2.35;
export const CELL_CENTRES: readonly number[] = [-2.6, 0, 2.6];
/** z of the barred cell fronts; the corridor is everything south of it. */
export const CELL_FRONT_Z = -0.35;

/**
 * Cell block (mission 32 starts in the middle cell): concrete, three cells along the rear wall with
 * barred fronts facing the camera, a bunk in each, and a corridor across the south. No roof over the
 * cells at all - only an eave along the rear wall.
 */
export const cellBlock: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  shell(mb, p, {
    halfW: 4, halfD: 3, h: 3.0, wall: 0.35, ramp: 'concrete', floor: 'concrete', roofDepth: 0.9, doorHalf: 0.8,
  });
  const rear = -3 + 0.35;
  for (const x of [-1.3, 1.3]) {
    mb.box(0.25, 2.6, CELL_FRONT_Z - rear, sided(p, 'concrete'), place(x, 0, (rear + CELL_FRONT_Z) / 2));
  }
  const bar = p.color('metal.mid');
  mb.box(7.3, 0.12, 0.12, p.color('metal.lo'), place(0, 2.4, CELL_FRONT_Z));
  CELL_CENTRES.forEach((cx, cell) => {
    // Bars 10 cm wide - the finest that survive 18 px/m - but 60 cm apart, not a real cell's 15: at 40 cm two
    // of them crossed the man inside and hid nearly half of him. The door is the east 0.8 m of each front.
    for (const dx of [-1.0, -0.4, 0.2]) mb.box(0.1, 2.4, 0.08, bar, place(cx + dx, 0, CELL_FRONT_Z));
    const hinge = cx + 0.3;
    // A door is two stiles and two rails, so it reads as a frame among the bars; the middle cell's hangs open.
    mb.with(place(hinge, 0, CELL_FRONT_Z, cell === 1 ? -1.25 : 0), () => {
      for (const dx of [0.05, 0.75]) mb.box(0.1, 2.2, 0.08, bar, place(dx, 0, 0));
      for (const y of [0.1, 2.1]) mb.box(0.8, 0.1, 0.1, p.color('metal.lo'), place(0.4, y, 0));
    });
    mb.box(0.8, 0.45, 1.9, { all: p.color('timber.lo'), top: p.color('canvas.mid') }, place(cx - 0.7, 0, rear + 1.0));
  });
  return mb;
};
